import { GraphScale, useGraphCtx } from '@mi-front/core';
import { styled } from '@mui/material/styles';
import classNames from 'classnames';
import { axisBottom, axisTop, select } from 'd3';
import moment from 'moment-timezone';
import { Component } from 'react';

const StyledAxisWrapper = styled('g')(({ theme }) => ({
  '& .tick': {
    fontSize: '11px',
    '& line': {
      stroke: theme.palette.grey[200],
      strokeWidth: '1px',
    },
    '& text': {
      fill: theme.palette.text.primary,
    },
  },
  '& .domain': {
    stroke: theme.palette.grey[200],
    fill: 'none',
    strokeWidth: '1px',
  },
  '& .text': {
    lineHeight: '1rem',
  },
}));

interface Props {
  readonly scale: GraphScale;
  readonly width: number;
  readonly type?: 'bottom' | 'top';
  readonly x?: number;
  readonly y?: number;
  readonly tz?: string;
  readonly axisId?: string;
  readonly hideSeconds?: boolean;
}

export class AxisXP extends Component<Props> {
  static defaultProps = {
    type: 'bottom',
    x: 0,
    y: 0,
  };

  private d3Axis: any;
  private root: any;

  componentDidMount() {
    this.applyScale();
  }

  componentDidUpdate() {
    this.applyScale();
  }

  applyScale = () => {
    const { scale, type, width, tz, hideSeconds } = this.props;

    if (type === 'bottom') {
      this.d3Axis = axisBottom(scale);
    } else {
      this.d3Axis = axisTop(scale);
    }

    // Set proper axis time format
    // If hideSeconds is true, always use HH:mm format
    // Otherwise, if the selected zoom range is lower than 13.2 minutes, show seconds as well
    const [from, to] = scale.domain();

    if (hideSeconds) {
      this.d3Axis.tickFormat((d: any) => {
        return (tz ? moment(d).tz(tz) : moment(d)).format('HH:mm');
      });
    } else {
      const rangeMinutes = (to.valueOf() - from.valueOf()) / (60 * 1000);

      if (rangeMinutes <= 13.2) {
        this.d3Axis.tickFormat((d: any) => {
          return (tz ? moment(d).tz(tz) : moment(d)).format('HH:mm:ss');
        });
      } else {
        this.d3Axis.tickFormat((d: any) => {
          return (tz ? moment(d).tz(tz) : moment(d)).format('HH:mm');
        });
      }
    }

    if (width < 500) {
      this.d3Axis.ticks(width / 100);
    } else {
      this.d3Axis.ticks(width / 90);
    }

    select(this.root).call(this.d3Axis);
  };

  render() {
    const { x, y, axisId } = this.props;

    return (
      <StyledAxisWrapper className={classNames('axis-x')}>
        <g
          id={axisId}
          ref={(el) => {
            this.root = el;
          }}
          transform={`translate(${x}, ${y})`}
        />
      </StyledAxisWrapper>
    );
  }
}

type PassedProps = Pick<Props, 'type' | 'x' | 'y' | 'axisId' | 'hideSeconds'>;

export function AxisX(p: PassedProps) {
  const { scale, width, tz } = useGraphCtx();

  return <AxisXP scale={scale} width={width} tz={tz} {...p} />;
}
